import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import SideDrawer, {
  FieldLabel,
  SummaryRow,
  fieldSx,
  primaryButtonSx,
} from "components/SideDrawer";
import { useToast } from "components/Toast";
import { DateField, TimeField } from "components/FormPickers";
import useDebounce from "hooks/useDebounce";
import { splitWallClock } from "utils/helpers";
import { Booking } from "services/manualBooking";
import {
  FlightFinance,
  ReschedulePayload,
  RescheduleType,
  formatNaira,
  getApiError,
  previewFlightReschedule,
  rescheduleFlight,
} from "services/finance";
import BookingSummaryCard from "./BookingSummaryCard";

const DEFAULT_SUBJECT = "Your WiaMove Flight Has Been Rescheduled";
const DEFAULT_BODY =
  "Your flight has been rescheduled. Please see your updated departure and arrival details below.";

const formatDateTime = (date?: string, time?: string) => {
  if (!date || !time) return "—";
  const d = new Date(`${date}T${time}:00`);
  if (isNaN(d.getTime())) return `${date} · ${time}`;
  return `${d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })} · ${d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`;
};

// Legs of the first segment, if the API exposes them; used for `legIndex`
// which the API requires when a segment has stopovers.
const getLegs = (finance?: FlightFinance) => {
  const segment: any = finance?.booking?.segments?.[0];
  const legs: any[] = segment?.legs || segment?.FlightLegs || [];
  return Array.isArray(legs) ? legs : [];
};

const RescheduleDrawer = ({
  open,
  onClose,
  booking,
  finance,
}: {
  open: boolean;
  onClose: () => void;
  booking: Booking;
  finance?: FlightFinance;
}) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const initial = useMemo(() => {
    const firstSegment = booking.segments?.[0];
    const dep = splitWallClock(
      booking.DepartureTime || firstSegment?.departureTime,
    );
    const arr = splitWallClock(booking.ArrivalTime || firstSegment?.arrivalTime);
    return {
      rescheduleType: "voluntary" as RescheduleType,
      legIndex: 0,
      newDepartureDate: dep.date,
      newDepartureTime: dep.time,
      newArrivalDate: arr.date,
      newArrivalTime: arr.time,
      clientPaid: "",
      supplierCharge: "",
      emailSubject: DEFAULT_SUBJECT,
      emailBody: DEFAULT_BODY,
    };
  }, [booking]);

  const [form, setForm] = useState(initial);
  // Reset when the drawer opens, during render rather than in an effect, so
  // the preview never fires once with the previous session's values.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setForm(initial);
  }

  const set = (key: keyof typeof form, value: any) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const legs = getLegs(finance);
  const isVoluntary = form.rescheduleType === "voluntary";

  const payload: ReschedulePayload = {
    rescheduleType: form.rescheduleType,
    legIndex: legs.length > 1 ? Number(form.legIndex) : undefined,
    newDepartureDate: form.newDepartureDate || undefined,
    newDepartureTime: form.newDepartureTime || undefined,
    newArrivalDate: form.newArrivalDate || undefined,
    newArrivalTime: form.newArrivalTime || undefined,
    clientPaid: isVoluntary && form.clientPaid !== "" ? Number(form.clientPaid) : undefined,
    supplierCharge:
      isVoluntary && form.supplierCharge !== "" ? Number(form.supplierCharge) : undefined,
  };

  // Debounce a serialized copy so a fresh object each render doesn't reset it.
  const payloadKey = JSON.stringify(payload);
  const debouncedKey = useDebounce(payloadKey, 400);
  const debouncedPayload: ReschedulePayload = JSON.parse(debouncedKey);
  const previewQuery = useQuery({
    queryKey: ["flightReschedulePreview", booking.id, debouncedKey],
    queryFn: () => previewFlightReschedule(booking.id, debouncedPayload),
    // Only preview settled input, never a value that's about to be replaced.
    enabled: open && debouncedKey === payloadKey,
    // The preview depends on the booking's current server state, which
    // changes after a reschedule; never serve a cached pre-change preview.
    staleTime: 0,
    retry: false,
    placeholderData: (prev) => prev,
  });
  const preview = previewQuery.data?.data;
  const previewError = previewQuery.error
    ? getApiError(previewQuery.error, "")
    : "";

  const mutation = useMutation({
    mutationFn: () =>
      rescheduleFlight(booking.id, {
        ...payload,
        emailSubject: form.emailSubject.trim(),
        emailBody: form.emailBody.trim(),
        sendEmail: true,
      }),
    onSuccess: (res) => {
      const email = res?.data?.email;
      if (email && !email.sent && !email.skipped) {
        showToast(
          res?.message ||
            `Reschedule saved, but the email failed${email.error ? `: ${email.error}` : ""}`,
          "warning",
        );
      } else {
        showToast(res?.message || "Booking rescheduled", "success");
      }
      queryClient.invalidateQueries({ queryKey: ["flightFinance", booking.id] });
      queryClient.invalidateQueries({ queryKey: ["finance"] });
      queryClient.invalidateQueries({ queryKey: ["transactions"] });
      onClose();
    },
    onError: (error: any) =>
      showToast(getApiError(error, "Failed to reschedule booking"), "error"),
  });

  const hasDeparture = Boolean(form.newDepartureDate && form.newDepartureTime);
  const hasArrival = Boolean(form.newArrivalDate && form.newArrivalTime);
  const canSubmit =
    (hasDeparture || hasArrival) &&
    form.emailSubject.trim() &&
    form.emailBody.trim() &&
    (!isVoluntary || (form.clientPaid !== "" && form.supplierCharge !== ""));

  const clientPaid = preview?.clientPaid ?? payload.clientPaid ?? 0;
  const supplierCharge = preview?.supplierCharge ?? payload.supplierCharge ?? 0;
  const revenue = preview?.rescheduleRevenue ?? clientPaid - supplierCharge;

  return (
    <SideDrawer open={open} onClose={onClose} title="Reschedule Booking">
      <BookingSummaryCard booking={booking} />

      <Typography fontSize={16} fontWeight={600} color="#101828" mb="8px">
        Reschedule Type
      </Typography>
      <TextField
        select
        fullWidth
        size="small"
        sx={fieldSx}
        value={form.rescheduleType}
        onChange={(e) => set("rescheduleType", e.target.value)}
      >
        <MenuItem value="voluntary">Voluntary Reschedule</MenuItem>
        <MenuItem value="airline">Airline Reschedule</MenuItem>
      </TextField>
      {!isVoluntary && (
        <Typography fontSize={12} color="#667085" mt="6px">
          Airline-initiated reschedules carry no charges.
        </Typography>
      )}

      {legs.length > 1 && (
        <Box mt="16px">
          <FieldLabel>Flight Leg</FieldLabel>
          <TextField
            select
            fullWidth
            size="small"
            sx={fieldSx}
            value={form.legIndex}
            onChange={(e) => set("legIndex", Number(e.target.value))}
          >
            {legs.map((leg: any, index: number) => (
              <MenuItem key={index} value={index}>
                Leg {index + 1}
                {leg?.DepartureCode && leg?.DestinationCode
                  ? `: ${leg.DepartureCode} → ${leg.DestinationCode}`
                  : ""}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      <Typography fontSize={16} fontWeight={600} color="#101828" mt="24px" mb="8px">
        New Flight Details
      </Typography>
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap="12px">
        <Box>
          <FieldLabel>New Departure Date</FieldLabel>
          <DateField
            value={form.newDepartureDate}
            onChange={(value) => set("newDepartureDate", value)}
          />
        </Box>
        <Box>
          <FieldLabel>New Departure Time</FieldLabel>
          <TimeField
            value={form.newDepartureTime}
            onChange={(value) => set("newDepartureTime", value)}
          />
        </Box>
        <Box>
          <FieldLabel>New Arrival Date</FieldLabel>
          <DateField
            value={form.newArrivalDate}
            onChange={(value) => set("newArrivalDate", value)}
            minDate={form.newDepartureDate}
          />
        </Box>
        <Box>
          <FieldLabel>New Arrival Time</FieldLabel>
          <TimeField
            value={form.newArrivalTime}
            onChange={(value) => set("newArrivalTime", value)}
          />
        </Box>
      </Box>

      <Typography fontSize={16} fontWeight={600} color="#101828" mt="24px">
        Customer Communication
      </Typography>
      <Typography fontSize={13} color="#667085" mb="12px">
        Edit the message before sending the reschedule notification to the customer.
      </Typography>
      <FieldLabel>Email Subject</FieldLabel>
      <TextField
        fullWidth
        size="small"
        sx={{ ...fieldSx, mb: "12px" }}
        value={form.emailSubject}
        onChange={(e) => set("emailSubject", e.target.value)}
      />
      <FieldLabel>Message Body</FieldLabel>
      <TextField
        fullWidth
        multiline
        rows={4}
        sx={fieldSx}
        value={form.emailBody}
        onChange={(e) => set("emailBody", e.target.value)}
      />

      {isVoluntary && (
        <>
          <Typography fontSize={16} fontWeight={600} color="#101828" mt="24px">
            Reschedule Revenue
          </Typography>
          <Typography fontSize={13} color="#667085" mb="12px">
            Enter the final revenue generated from this reschedule.
          </Typography>
          <FieldLabel>Amount Client Paid</FieldLabel>
          <TextField
            fullWidth
            size="small"
            type="number"
            sx={{ ...fieldSx, mb: "12px" }}
            value={form.clientPaid}
            onChange={(e) => set("clientPaid", e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">₦</InputAdornment>,
            }}
          />
          <FieldLabel>Wakanow Charge</FieldLabel>
          <TextField
            fullWidth
            size="small"
            type="number"
            sx={fieldSx}
            value={form.supplierCharge}
            onChange={(e) => set("supplierCharge", e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">₦</InputAdornment>,
            }}
          />

          <Box bgcolor="#F9FAFB" borderRadius="8px" p="12px 16px" mt="16px">
            <SummaryRow label="Client paid" value={formatNaira(clientPaid)} />
            <SummaryRow label="Wakanow charge" value={formatNaira(supplierCharge)} />
            <SummaryRow label="Reschedule Revenue" value={formatNaira(revenue)} bold />
          </Box>
        </>
      )}

      <Box bgcolor="#F9FAFB" borderRadius="8px" p="12px 16px" mt="16px">
        <SummaryRow
          label="Reschedule Type"
          value={isVoluntary ? "Voluntary Reschedule" : "Airline Reschedule"}
        />
        <SummaryRow
          label="New Departure"
          value={formatDateTime(form.newDepartureDate, form.newDepartureTime)}
        />
        <SummaryRow
          label="New Arrival"
          value={formatDateTime(form.newArrivalDate, form.newArrivalTime)}
        />
      </Box>

      {previewError && (
        <Typography fontSize={13} color="#D92D20" mt="12px">
          {previewError}
        </Typography>
      )}

      <Divider sx={{ my: "24px" }} />
      <Button
        fullWidth
        variant="contained"
        sx={primaryButtonSx}
        disabled={!canSubmit || mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        {mutation.isPending ? "Sending..." : "Send Reschedule Mail"}
      </Button>
      <Button
        fullWidth
        onClick={onClose}
        sx={{ mt: "8px", color: "#101828", textDecoration: "underline", textTransform: "none" }}
      >
        Cancel
      </Button>
    </SideDrawer>
  );
};

export default RescheduleDrawer;
